// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';
import clamp from 'lodash/clamp';
import {selx} from 'clsx';

import {SvgIconEl} from 'common/components/svg-icon';

import {ButtonEl} from '../button';
import {ButtonSize, ButtonTheme} from '../button/constants';
import {TooltipEl} from '../tooltip';
import {SpinnerEl} from '../spinner';
import {InputLabelEl} from './label';
import type {InputComponent} from './index';
import {InputChangeEvent, InputInputEvent, InputType, ValidationStatus} from './types';
import styles from './index.module.styl';

function RightIcon({cmp}: {cmp: InputComponent}) {
  if (cmp.attr(`hide-right-icon`)) {
    return null;
  }
  const icon = {valid: `circle-checkmark`, invalid: `warning`}[cmp.attr(`validation-status`)];
  if (cmp.attr(`validation-status`)) {
    return (
      <div
        sel={selx(styles.mpInputRightIcon, {
          [styles.valid]: cmp.attr(`validation-status`) === `valid`,
          [styles.invalid]: cmp.attr(`validation-status`) === `invalid`,
        })}
      >
        <SvgIconEl attrs={{icon}} />
      </div>
    );
  } else if (cmp.attr(`always-show-clear`) || (cmp.state.value && cmp.attr(`show-clear`))) {
    return (
      <div
        sel={selx(styles.mpInputRightIcon, styles.clickable)}
        on={{
          click: (ev) => cmp.handleClearInput(ev),
        }}
      >
        <SvgIconEl attrs={{icon: `circle-clear`}} sel={selx(styles.mpInputClickableIcon)} />
      </div>
    );
  } else if (cmp.attr(`pre-text`) && cmp.attr(`help-text`)) {
    return (
      <div sel={selx(styles.mpInputRightIcon)}>
        <SvgIconEl attrs={{icon: `info-xs`}} />
        <TooltipEl attrs={{text: cmp.attr(`help-text`)}} />
      </div>
    );
  }
  return null;
}

function NumericControls({cmp}: {cmp: InputComponent}) {
  return (
    <div sel={selx(styles.mpInputNumericArrowControls)}>
      <ButtonEl
        sel={selx(styles.numericArrowButton)}
        attrs={{
          theme: ButtonTheme.Tertiary,
          size: ButtonSize.Tiny,
          icon: `caret-up`,
        }}
        on={{
          mousedown: (ev) => {
            // using onmousedown and preventDefault() here instead of onclick to keep focus on the input
            ev.preventDefault();
            cmp.update({
              value: String(
                clamp(parseInt(cmp.state.value, 10) + 1, parseInt(cmp.attr(`min`), 10), parseInt(cmp.attr(`max`), 10)),
              ),
            });
            cmp.debouncedChange({
              ...ev,
              target: cmp.searchInput,
            });
          },
        }}
      />
      <ButtonEl
        sel={selx(styles.numericArrowButton)}
        attrs={{
          theme: ButtonTheme.Tertiary,
          size: ButtonSize.Tiny,
          icon: `caret-down`,
        }}
        on={{
          mousedown: (ev) => {
            // using onmousedown and preventDefault() here instead of onclick to keep focus on the input
            ev.preventDefault();
            cmp.update({
              value: String(
                clamp(parseInt(cmp.state.value, 10) - 1, parseInt(cmp.attr(`min`), 10), parseInt(cmp.attr(`max`), 10)),
              ),
            });
            cmp.debouncedChange({
              ...ev,
              target: cmp.searchInput,
            });
          },
        }}
      />
    </div>
  );
}

export function Template(this: InputComponent) {
  const {value} = this.state;
  const validationStatus = this.attr(`validation-status`);
  const isValid = validationStatus === ValidationStatus.Valid;
  const isInvalid = validationStatus === ValidationStatus.Invalid;

  return (
    <div
      sel={selx(styles.mpInputContainer, {
        [styles.themeWhite]: this.attr(`theme`) === `white`,
      })}
    >
      {this.attr(`label`) ? (
        <InputLabelEl
          sel={selx(styles.inputLabel)}
          attrs={{
            label: this.attr(`label`),
            'field-id': this.attr(`field-id`),
            necessary: this.attr(`necessary`),
            'help-text': this.attr(`help-text`),
            'extra-info': this.attr(`extra-info`),
          }}
          on={{
            click: () => this.attr(`disabled`) === false && this.focus(),
          }}
        />
      ) : null}
      <div
        sel={selx(styles.mpInputFieldContainer, {
          [styles.hasMinInputWidth]: Boolean(this.attr(`min-input-width`)),
          [styles.hasMultilinePrefix]: this.attr(`multiline-prefix`),
          [styles.hideFocusStyling]: this.attr(`hide-focus-styling`),
          [styles.isDisabled]: this.attr(`disabled`),
          [styles.sizeSmall]: this.attr(`size`) === `small`,
          [styles.sizeMedium]: this.attr(`size`) === `medium`,
          [styles.sizeLarge]: this.attr(`size`) === `large`,
          [styles.typeNumber]: this.attr(`type`) === `number`,
          [styles.typePassword]: this.attr(`type`) === `password`,
          [styles.typeText]: this.attr(`type`) === `text`,
          [styles.isValid]: isValid,
        })}
        // For whatever reason we have to leave this as a class or
        // userEvent.type doesn't work breaking a bunch of Storybook tests...
        // Doesn't really matter since they'll all be lumped up again when
        // this is converted to React
        class={{
          [styles.isInvalid]: isInvalid,
        }}
        on={{
          click: () => this.focus(),
        }}
      >
        {(() => {
          const TagName = this.attr(`type`) === `textarea` ? `textarea` : `input`;

          return (
            <>
              <div sel={selx(styles.mpInputLeftArea)}>
                {this.attr(`icon`) && (
                  <div sel={selx(`mp-input-left-icon`)}>
                    {this.attr(`icon`) === `spinner` ? (
                      <SpinnerEl sel={selx(styles.mpInputLeftIcon)} />
                    ) : (
                      <SvgIconEl attrs={{icon: this.attr(`icon`)}} sel={selx(styles.mpInputLeftIcon)} />
                    )}
                  </div>
                )}
                {this.attr(`pre-text`) && (
                  <div
                    sel={selx(styles.mpInputPreText, {
                      [styles.sizeSmall]: this.attr(`size`) === `small`,
                      [styles.sizeMedium]: this.attr(`size`) === `medium`,
                      [styles.sizeLarge]: this.attr(`size`) === `large`,
                    })}
                    on={{click: () => this.focus()}}
                  >
                    {this.attr(`pre-text`)}
                  </div>
                )}
              </div>
              <div
                sel={selx(styles.mpInputWrapper)}
                hook={{insert: ({elm}) => this.handleInputWrapperInsert(elm as HTMLElement)}}
              >
                {this.params.prefixRenderer &&
                  (this.attr(`multiline-prefix`) ? (
                    this.params.prefixRenderer()
                  ) : (
                    <div hook={{insert: ({elm}) => this.handleInputPrefixInsert(elm as HTMLElement)}}>
                      {this.params.prefixRenderer()}
                    </div>
                  ))}
                <TagName
                  sel={selx(styles.mpInput)}
                  style={{
                    'text-align': this.attr(`text-align`),
                    ...(this.attr(`min-input-width`) && {'min-width': `${this.attr(`min-input-width`)}px`}),
                    ...(this.attr(`multiline-prefix`) && {
                      width: this.state.textWidth ? `${this.state.textWidth}px` : `auto`,
                    }),
                  }}
                  attrs={{
                    ...(this.attr(`ariaLabel`) ? {[`aria-label`]: this.attr(`ariaLabel`)} : {}),
                    autocomplete: this.attr(`autocomplete`),
                    elref: `inputTag`,
                    id: this.attr(`field-id`),
                    name: this.attr(`field-name`),
                    placeholder: this.attr(`placeholder`),
                    disabled: this.attr(`disabled`),
                    readonly: this.attr(`readonly`),
                    size: 1,
                    // We do custom comma formatting on number type inputs.
                    // Browsers won't accept something like '1,000' as a valid
                    // do do not use the "type" attribute in this case.
                    type: TagName === `input` && this.attr(`type`) !== InputType.Number ? this.attr(`type`) : null,
                    maxlength: this.attr(`maxlength`),
                    minlength: TagName === `input` ? this.attr(`min`) : null,
                    min: this.supportsMinMaxAttrs() ? this.attr(`min`) : null, // only enforces arrow key decrements
                    max: this.supportsMinMaxAttrs() ? this.attr(`max`) : null, // only enforces arrow key increments
                    step: this.supportsMinMaxAttrs() ? this.attr(`step`) : null, // only enforces arrow key increments
                    'not-focusable': this.attr(`not-focusable`),
                  }}
                  props={{
                    value: this.displayValue,
                  }}
                  on={{
                    click: (ev) => this.handleClick(ev),
                    input: (ev: InputInputEvent) => this.handleInput(ev),
                    blur: (ev) => this.handleBlur(ev),
                    focus: (ev) => this.handleFocus(ev),
                    change: (ev: InputChangeEvent) => this.handleChange(ev),
                    keydown: (ev) => this.handleKeydown(ev),
                    keyup: (ev) => this.handleKeyup(ev),
                    paste: (ev) => this.handlePaste(ev),
                  }}
                  hook={{
                    insert: ({elm}) => {
                      this.handleSearchInputInsert(elm as HTMLInputElement);
                    },
                  }}
                />
              </div>
              <div sel={selx(styles.mpInputRightArea)}>
                {this.params.suffixRenderer?.()}
                {this.attr(`action-text`) && (this.attr(`always-show-action-text`) || value) ? (
                  <div sel={selx(styles.mpInputActionText)}>{this.attr(`action-text`)}</div>
                ) : null}
                {this.attr(`type`) === InputType.Number && this.attr(`show-numeric-input-controls`) && (
                  <NumericControls cmp={this} />
                )}
                <RightIcon cmp={this} />
              </div>
            </>
          );
        })()}
      </div>
      {this.attr(`validation-status`) === `invalid` && this.attr(`error-message`) ? (
        <div attrs={{elref: `inputErrorMessage`}} sel={selx(styles.mpInputErrorMessage)}>
          {this.attr(`error-message`)}
        </div>
      ) : null}
    </div>
  );
}
